import { WORKOUT_PRIVATE_ACCESS_PENDING_TOKEN_KEY } from "./workoutPrivateAccessConfig";

export const HEALTHY_HABITS_ORIGIN = "https://healthy-habits.joshtillo.ca";
export const LEGACY_WORKOUT_ACCESS_PATH = "/workout_plan/access";
export const LEGACY_HANDOFF_PARAMETER = "legacy_handoff";
export const HANDOFF_READY = "healthy-habits.workout-access-handoff.ready.v1";
export const HANDOFF_DELIVER = "healthy-habits.workout-access-handoff.deliver.v1";
export const HANDOFF_ACCEPTED = "healthy-habits.workout-access-handoff.accepted.v1";
export const HANDOFF_FAILED = "healthy-habits.workout-access-handoff.failed.v1";
export const HANDOFF_TIMEOUT_MS = 16000;
const V2_BEARER_PATTERN = /^[A-Za-z0-9_-]{43}$/;

export function isValidLegacyV2Bearer(value) {
  return typeof value === "string" && V2_BEARER_PATTERN.test(value);
}

export function buildHealthyHabitsDestination({ pathname, search = "", hash = "" }) {
  const destination = new URL(`${HEALTHY_HABITS_ORIGIN}${pathname}`);
  const params = new URLSearchParams(search);
  params.delete("token");
  destination.search = params.toString();
  destination.hash = hash;
  return destination.href;
}

export function readPendingLegacyBearer(storage) {
  if (!storage || typeof storage.getItem !== "function") return null;
  try {
    const value = storage.getItem(WORKOUT_PRIVATE_ACCESS_PENDING_TOKEN_KEY);
    return typeof value === "string" && value.trim() ? value.trim() : null;
  } catch (_error) {
    return null;
  }
}

export function generateLegacyHandoffNonce(
  cryptoRef = typeof window === "undefined" ? null : window.crypto
) {
  if (!cryptoRef || typeof cryptoRef.getRandomValues !== "function") {
    throw new Error("Secure browser randomness is unavailable.");
  }
  const bytes = cryptoRef.getRandomValues(new Uint8Array(32));
  let binary = "";
  bytes.forEach((byte) => {
    binary += String.fromCharCode(byte);
  });
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/g, "");
}

export function createLegacyHandoffAttempt({ bearer: initialBearer, nonce, windowRef, storageRef, onState, timeoutMs = HANDOFF_TIMEOUT_MS, childPollMs = 250 }) {
  let bearer = initialBearer;
  let childWindow = null;
  let phase = "opening";
  let active = true;
  let timeoutId = null;
  let childPollId = null;

  function cleanup({ closeChild = false } = {}) {
    windowRef.removeEventListener("message", handleMessage);
    if (timeoutId !== null) windowRef.clearTimeout(timeoutId);
    if (childPollId !== null) windowRef.clearInterval(childPollId);
    timeoutId = null;
    childPollId = null;
    active = false;
    bearer = null;
    if (closeChild && childWindow && !childWindow.closed) {
      try { childWindow.close(); } catch (_error) { /* Child may be outside script control. */ }
    }
  }

  function fail(reason) {
    if (!active) return;
    cleanup();
    onState({ status: "failed", reason });
  }

  function handleMessage(event) {
    if (!active || event.origin !== HEALTHY_HABITS_ORIGIN || event.source !== childWindow || !event.data || event.data.nonce !== nonce) return;

    if (event.data.type === HANDOFF_READY && phase === "awaiting-ready") {
      try {
        childWindow.postMessage({ type: HANDOFF_DELIVER, nonce, bearer }, HEALTHY_HABITS_ORIGIN);
        phase = "awaiting-acceptance";
        onState({ status: "transferring" });
      } catch (_error) { fail("transfer-failed"); }
      return;
    }

    if (event.data.type === HANDOFF_ACCEPTED && phase === "awaiting-acceptance") {
      try { storageRef.removeItem(WORKOUT_PRIVATE_ACCESS_PENDING_TOKEN_KEY); }
      catch (_error) { fail("cleanup-failed"); return; }
      cleanup();
      onState({ status: "accepted" });
      return;
    }

    if (event.data.type === HANDOFF_FAILED && (phase === "awaiting-ready" || phase === "awaiting-acceptance")) fail("receiver-failed");
  }

  windowRef.addEventListener("message", handleMessage);
  const receiverUrl = `${HEALTHY_HABITS_ORIGIN}${LEGACY_WORKOUT_ACCESS_PATH}?${LEGACY_HANDOFF_PARAMETER}=${encodeURIComponent(nonce)}`;
  try { childWindow = windowRef.open(receiverUrl, "_blank"); }
  catch (_error) { fail("popup-blocked"); return { dispose: cleanup, receiverUrl }; }
  if (!childWindow) { fail("popup-blocked"); return { dispose: cleanup, receiverUrl }; }

  phase = "awaiting-ready";
  onState({ status: "waiting" });
  timeoutId = windowRef.setTimeout(() => fail("timeout"), timeoutMs);
  childPollId = windowRef.setInterval(() => { if (childWindow.closed) fail("child-closed"); }, childPollMs);
  return { receiverUrl, dispose(options) { if (active) cleanup(options); } };
}
